import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, List, ListItem, ListItemIcon, ListItemText,
  Typography, FormControlLabel, Checkbox,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { useTranslation } from 'react-i18next';
import { DriveFile } from '../services/myDriveService';

const MAX_SHOWN = 10;

interface Props {
  open: boolean;
  files: DriveFile[];
  onClose: () => void;
  onConfirm: (recursive: boolean) => void;
}

export default function BatchDeleteDialog({ open, files, onClose, onConfirm }: Props) {
  const { t } = useTranslation();
  const [recursive, setRecursive] = useState(false);
  const hasFolder = files.some(f => f.is_directory);
  const shown = files.slice(0, MAX_SHOWN);
  const extra = files.length - shown.length;

  const handleClose = () => { setRecursive(false); onClose(); };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        {t('myDrive.batchDeleteTitle', '確定刪除 {count} 個項目?')
          .replace('{count}', String(files.length))}
      </DialogTitle>
      <DialogContent>
        <List dense disablePadding>
          {shown.map(f => (
            <ListItem key={f.id} disablePadding>
              <ListItemIcon sx={{ minWidth: 28 }}>
                {f.is_directory
                  ? <FolderIcon fontSize="small" color="warning" />
                  : <InsertDriveFileIcon fontSize="small" />}
              </ListItemIcon>
              <ListItemText primary={f.filename} primaryTypographyProps={{ variant: 'body2' }} />
            </ListItem>
          ))}
          {extra > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
              {t('myDrive.andMore', '等 {count} 個項目').replace('{count}', String(extra))}
            </Typography>
          )}
        </List>
        {hasFolder && (
          <FormControlLabel
            sx={{ mt: 1 }}
            control={<Checkbox checked={recursive} onChange={e => setRecursive(e.target.checked)} />}
            label={
              <Typography variant="body2">
                {t('myDrive.batchDeleteRecursive', '遞迴刪除非空資料夾')}
              </Typography>
            }
          />
        )}
        {hasFolder && !recursive && (
          <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 0.5 }}>
            {t('myDrive.batchDeleteWarn', '包含非空資料夾，請勾選遞迴刪除')}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t('common.cancel', '取消')}</Button>
        <Button variant="contained" color="error"
                onClick={() => { onConfirm(recursive); setRecursive(false); }}>
          {t('myDrive.delete', '刪除')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
